<script setup lang="ts">
import StarsImage from '@/assets/images/star-group.png'
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import CrossIcon from '@/components/icons/CrossIcon.vue'
import { computed } from 'vue'
import { DateTime } from 'luxon'
import type { PuzzleChallengeWinnerNotification } from '@/types/shared/player'
import { useAuth } from '@/composables/auth'
import { usePuzzleChallenge } from '@/composables/game/challenges'
import PuzzleChallengeScoreImage from '@/assets/images/icons/timer-icon.webp'
import ChallengeBoardCard2 from '@/components/ChallengeBoardCard2.vue'
import { availableBadges } from '@/composables/game/badge'
import { formatMillisecondDuration } from '@/helpers/date'
import { CHALLENGE_PRIZE_TYPE } from '@/types/shared/player-config'

const props = defineProps<{
	show: boolean,
	notification: PuzzleChallengeWinnerNotification
}>()

defineEmits(['hide'])

const { getPrizeTypeImage, getCardMedal } = usePuzzleChallenge()
const { profile } = useAuth()

const formattedDate = computed(() => {
  return DateTime.fromSeconds(props.notification.date).toFormat('ccc dd LLLL yyyy')
})
</script>

<template>
  <TheBottomSheet
    :show="show"
    centered
    base-modal-class="daily-up-challenge-modal green-shadow overflow-visible rounded-[32px] mt-10"
    :content-class="`transition-[background] duration-300 overflow-visible px-0 pt-[1px] flex flex-col bg-[linear-gradient(180deg,_#FF0533_0.01%,_#5A83A7_21.93%,_#000_36.54%)]`"
    :outer-content-class="`transition-[background] duration-300 overflow-visible bg-[linear-gradient(180deg,_#FF0533_0.01%,_#5A83A7_21.93%,_#000_36.54%)]`"
    border-container-class="overflow-visible"
    @hide="$emit('hide')"
  >
    <template #closeButton>
      <div
        class="absolute w-6 h-6 text-black top-0 right-0 z-[5] bg-[#FF3737] rounded-full flex items-center justify-center"
        @click="$emit('hide')"
      >
        <CrossIcon class="text-white [&>path]:stroke-[3px] size-[14px]" />
      </div>
    </template>
    <template #baseContent>
      <div
        class="h-0 w-full relative"
        @click="$emit('hide')"
      >
        <img
          class="h-auto w-full absolute top-full left-1/2 -translate-x-1/2 -translate-y-[60%]"
          src="@/assets/images/puzzle-challenge-modal-background.webp"
        >
        <img
          src="@/assets/images/puzzle-challenge-icon.webp"
          class="size-[225px] absolute top-0 left-1/2 -translate-x-1/2 -translate-y-[100px] z-[101] transition-all duration-300"
        >
        <div
          class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 size-[50px] bg-[#3B9A7C] blur-[27px] rounded-full z-[100] transition-opacity duration-300"
        />
        <img
          :src="StarsImage"
          class="h-[138px] w-auto absolute top-0 left-1/2 -translate-x-1/2 -translate-y-[78px] z-[101]"
        >
      </div>
    </template>
    <div class="flex flex-col items-center relative overflow-hidden pt-[80px]">
      <div class="px-6 text-center text-[36px] leading-9 font-bold">
        Yay! You’re a<br>
        Jigsaw Winner!
      </div>
      <div class="mt-6 -mx-6 h-7 w-[calc(100%+48px)] bg-red-countdown-banner flex items-center justify-center">
        <div class="uppercase text-white text-base font-bold text-shadow-default pr-1.5">
          Ended
        </div>
        <img
          class="size-[18px] object-cover"
          src="@/assets/images/gold-clock.png"
        >
        <div class="ml-1 golden-text-black-stroke text-base font-bold gold-text-shadow px-0.5">
          {{ formattedDate }}
        </div>
      </div>
      <div class="px-6 mt-6 text-center text-light-white/70 text-base leading-5 -tracking-[0.32px] font-medium">
        The following reward has been sent to your account:
      </div>

      <div class="px-6 mt-6 w-full">
        <ChallengeBoardCard2
          :player="profile"
          :index="(notification.position).toString()"
          :medal="getCardMedal(notification.position)"
          :prize-value="'amount' in notification ? notification.amount : 0"
          :prize-name="'nft' in notification ? notification.nft : ''"
          :prize-image="'lottieUrl' in notification ? notification.lottieUrl : getPrizeTypeImage(notification.prizeType, notification.specialCurrencyId)"
          :score-value="formatMillisecondDuration(notification.score)"
          :score-image="PuzzleChallengeScoreImage"
          :is-prize-pool-nft="notification.prizeType === CHALLENGE_PRIZE_TYPE.NFT"
          :is-prize-pool-badge="notification.prizeType === CHALLENGE_PRIZE_TYPE.BADGE"
          :badge="availableBadges.find((badge) => badge.id === notification.badgeId)"
          class="card"
        />
      </div>
    </div>
  </TheBottomSheet>
</template>

<style scoped lang="scss">
@import "@/assets/styles/challenges";

.gold-text {
	background: linear-gradient(90deg, #FFF246 0%, #E5B80B 37%, #FFF571 68.5%, #E5B80B 100%);
	background-clip: text;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
}
</style>